import React, { useMemo } from 'react';
import type { MRT_ColumnDef } from '@talentmesh/table';
import ScoreCell from '@/Components/Table/ScoreCell';
import InfoTooltip from '@/Components/Tooltip/InfoTooltip';
import UIStrings from '@/Utils/UIStrings';
import { TextCell } from '../../../../../Overview/Contexts/CandidatesTableContext/cells';
import { IdentifiedProfileResponse } from '../../../../api/DTO/IdentifiedProfileResponse';
import { useSearchCriteria } from '../../../../api/useSearchCriteria';
import CompanyCell from '../../../TableCells/CompanyCell';
import PersonalInfoCell from './PersonalInfoCell';
import CandidateCell from './CandidateCell';
import GuidHelper from '../../../../../../../Utils/GuidHelper';

export type IdentifiedCandidatesCol = MRT_ColumnDef<IdentifiedProfileResponse>;
export type CellParams = Parameters<Extract<IdentifiedCandidatesCol['Cell'], Function>>['0'];
export type HeadParams = Parameters<Extract<IdentifiedCandidatesCol['Header'], Function>>['0'];

const useColumns = () => {
    const { data } = useSearchCriteria();

    return useMemo(() => {
        const columns: IdentifiedCandidatesCol[] = [
            {
                accessorKey: 'profile.name',
                header: UIStrings.Candidate,
                enableSorting: false,
                Cell: ({ row }: CellParams) => <CandidateCell {...row.original} />,
                size: 200,
            },
            {
                id: 'personalInfo',
                header: UIStrings.PersonalInfo,
                Cell: ({ row }: CellParams) => <PersonalInfoCell {...row.original} />,
                size: 150,
            },
            {
                id: 'company',
                header: UIStrings.Company,
                Cell: ({ row }: CellParams) => <CompanyCell {...row.original} />,
                size: 220,
            },
            {
                accessorKey: 'profile.position',
                header: UIStrings.JobTitle,
                Cell: ({ row }: CellParams) => (
                    <TextCell value={row.original.profile.experience?.at(0)?.title || row.original.profile.position} />
                ),
                size: 400,
                enableSorting: false,
            },
            {
                id: 'location',
                accessorFn: (row) => row.profile.currentJobLocationGeocode?.title,
                header: UIStrings.Location,
                Cell: ({ row, renderedCellValue }: CellParams) => (
                    <TextCell value={renderedCellValue || row.original.profile.locationGeocode?.title} />
                ),
                size: 270,
                enableSorting: false,
            },
            {
                accessorKey: 'matchScore',
                header: UIStrings.MatchScore,
                Cell: ({ row }: CellParams) => (
                    <ScoreCell level={row.original.matchScore?.scoreLevel} score={row.original.matchScore?.score} />
                ),
                size: 170,
            },
        ];

        if (data?.criteria.length) {
            for (let i = 0; i < data.criteria.length; i++) {
                // When recruitment is created without criteria then guid will be empty for all columns.
                // This will cause key duplication issue on the table. To avoid that we will use index based key when guid is empty.
                const criteriaColId =
                    data.criteria[i].id !== GuidHelper.Empty ? data.criteria[i].id : `criteriaScore-${i}`;

                columns.push({
                    id: criteriaColId,
                    accessorFn: (row) => row.searchCriteriaScores?.[i]?.score,
                    header: `${UIStrings.Criteria(i + 1)}`,
                    Header: ({ column }: HeadParams) => (
                        <div className="flex items-center gap-1">
                            {column.columnDef.header}
                            <InfoTooltip description={data.criteria[i].criterion} />
                        </div>
                    ),
                    Cell: ({ row }: CellParams) => {
                        const criteriaRow = row.original.searchCriteriaScores?.find(
                            ({ searchCriteriaId }) => searchCriteriaId === criteriaColId,
                        );

                        return <ScoreCell level={criteriaRow?.scoreLevel} score={criteriaRow?.score} />;
                    },
                    size: 180,
                });
            }
        }

        return columns;
    }, [data]);
};

export default useColumns;
